import React from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { Field, inputClass } from '../../components/ui/Field';
import { AuthShell } from '../../components/portal/AuthShell';
import { usePortalAuth } from '../../context/PortalAuth';
import { orgName } from '../../context/PortalOrg';

/** First sign in for an invited user: name and job title. The organisation comes from the invitation. */
export function PortalWelcome() {
  const {
    user,
    memberships,
    loading,
    completeOnboarding
  } = usePortalAuth();
  const navigate = useNavigate();
  const [values, setValues] = React.useState({
    firstName: '',
    lastName: '',
    jobTitle: ''
  });
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const [error, setError] = React.useState<string | null>(null);
  const [pending, setPending] = React.useState(false);
  if (loading) return null;
  if (!user) return <Navigate to="/portal/login" replace data-id="element-3283" data-mp="3c1bwy5zpnl0b" />;
  if (user.firstName) return <Navigate to="/portal/dashboard" replace data-id="element-3284" data-mp="wxjq2s1wyb4x" />;
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setPending(true);
    setError(null);
    const result = await completeOnboarding(values);
    setPending(false);
    if (result.ok) {
      navigate('/portal/dashboard', {
        replace: true
      });
      return;
    }
    setErrors(result.errors ?? {});
    if (result.error) setError(result.error);
    const first = ['firstName', 'lastName', 'jobTitle'].find(key => result.errors?.[key]);
    if (first) document.getElementById(`welcome-${first}`)?.focus();
  };
  const set = (key: keyof typeof values) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setValues(v => ({
      ...v,
      [key]: event.target.value
    }));
    setErrors(e => ({
      ...e,
      [key]: ''
    }));
  };
  return <AuthShell title="Welcome to the client portal" data-id="element-3285" data-mp="1ymwvuv6h34gw">
      <h1 className="font-display text-[30px] font-light text-ink" data-id="element-3286" data-mp="1ymn8lfctbbew">Welcome to the portal</h1>
      <p className="mt-3 text-[16px] text-body" data-id="element-3287" data-mp="1toloqs7kb82d">Tell us who you are so the team knows who they are working with. This takes a moment.</p>

      <div className="mt-6 rounded-md border border-line bg-panel px-5 py-4 text-[14px]" data-id="element-3288" data-mp="3sv0q7no777mb">
        <p className="text-muted" data-id="element-3289" data-mp="3swu15f5uge04">Signed in as</p>
        <p className="mt-0.5 text-ink" data-id="element-3290" data-mp="3e75wc3ygex7n">{user.email}</p>
        {memberships.length > 0 && <>
            <p className="mt-3 text-muted" data-id="element-3291" data-mp="3j5zei9oorce5">Invited to</p>
            <p className="mt-0.5 text-ink" data-id="element-3292" data-mp="apnqu365m2ae">{memberships.map(m => orgName(m.orgId)).join(', ')}</p>
          </>}
      </div>

      <form onSubmit={submit} noValidate className="mt-7 space-y-5" data-id="element-3293" data-mp="fnfu1tc91atw">
        {error && <p role="alert" className="text-[14px] text-[#FF8B7A]" data-id="element-3294" data-mp="1no896fhqq5c">
            {error}
          </p>}
        <div className="grid gap-5 sm:grid-cols-2" data-id="element-3295" data-mp="5p01dlxkptia">
          <Field id="welcome-firstName" label="First name" required error={errors.firstName || undefined} data-id="element-3296" data-mp="2meyifbac1ywy">
            <input id="welcome-firstName" autoComplete="given-name" value={values.firstName} onChange={set('firstName')} className={inputClass} aria-invalid={errors.firstName ? true : undefined} data-id="element-3297" data-mp="2qgab5poi709d" />
          </Field>
          <Field id="welcome-lastName" label="Last name" required error={errors.lastName || undefined} data-id="element-3298" data-mp="2dyg1xy4nqjwe">
            <input id="welcome-lastName" autoComplete="family-name" value={values.lastName} onChange={set('lastName')} className={inputClass} aria-invalid={errors.lastName ? true : undefined} data-id="element-3299" data-mp="2svgu22qrggve" />
          </Field>
        </div>
        <Field id="welcome-jobTitle" label="Job title" required error={errors.jobTitle || undefined} data-id="element-3300" data-mp="207b491kuo1op">
          <input id="welcome-jobTitle" autoComplete="organization-title" value={values.jobTitle} onChange={set('jobTitle')} className={inputClass} aria-invalid={errors.jobTitle ? true : undefined} data-id="element-3301" data-mp="1v8zwqxi1239n" />
        </Field>
        <Button type="submit" size="lg" disabled={pending} className="w-full" data-id="element-3302" data-mp="254babchp94ft">
          {pending ? 'Saving' : 'Continue to the portal'}
        </Button>
      </form>
    </AuthShell>;
}